import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as Cookies from 'js-cookie';
import SearchInput from 'components/forms/SearchInput';
import HideFields from 'components/Coverage/HideFields';
import HelpOutlineRoundedIcon from '@material-ui/icons/HelpOutlineRounded';
import Select from 'components/forms/Select';
import TableSortLabel from 'components/tables/tableSortLabel';
import Table from 'components/tables';
import TableHead from 'components/tables/tableHead';
import TableBody from 'components/tables/tableBody';
import TableCell from 'components/tables/tableCell';
import LoadingIcon from 'mdi-react/LoadingIcon';
import TableRow from 'components/tables/tableRow';
import Checkbox from 'components/forms/Checkbox';
import NoResults from 'components/NoResults';
import ButtonMenu from 'components/buttons/ButtonMenu';
import MenuItem from 'components/Menu/MenuItem';
import { isEmpty, first } from 'lodash';
import {
  CampaignsFilter,
  SequencesFilter,
  DynamicFilter,
} from 'components/filters';
import Pagination from 'components/pagination/Pagination';
import { downloadBlob, flattenKeyPairArray, uuidv4 } from 'utils/misc';
import {
  REPLY_STATUS_OPTIONS,
  EMAIL_VALIDATION_STATUS_OPTIONS,
  DELIVERY_STATUS_OPTIONS,
  DELIVERY_STATUS_CRITERIA_OPTIONS,
} from 'constants/outreach';

import { getAccountSequences } from 'redux/actions/sequenceActions';
import withContactList from 'components/ContactList/withContactList';
import ContactUpdateDrawer from 'components/contact/ContactUpdateDrawer';
import axios from 'axios';
import { apiExportAccountSequenceContacts } from 'constants/apiRouting';
import ContactStatsSlim from 'containers/Sequences/SequenceDetail/ContactsTab/ContactStatsSlim';
import ContactRow from './ContactRow';
import DeleteBulkModal from './DeleteBulkModal';
import BlacklistBulkModal from './BlacklistBulkModal';


const DYNAMIC_MENU_OPTIONS = {
  campaign: 'campaign',
  sequence: 'sequence_ids',
  category: 'category',
  deliveryStatus: 'deliveryStatuses',
  validationStatus: 'validationStatuses',
  search: 'search',
};

const DEFAULT_DELIVERY_STATUS_CRITERIA = first(DELIVERY_STATUS_CRITERIA_OPTIONS);

class AccountContactList extends Component {
  static propTypes = {
    account: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    user: PropTypes.shape({
      email: PropTypes.string,
    }).isRequired,
    accountContacts: PropTypes.shape({
      count: PropTypes.number,
    }).isRequired,
    sequences: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
    }).isRequired,
    contacts: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    getContacts: PropTypes.func.isRequired,
    isSelectedAllContacts: PropTypes.bool.isRequired,
    showContactReplyForCurrentSequence: PropTypes.bool,
    getSelectedContactsOnPage: PropTypes.func.isRequired,
    handleSelectClick: PropTypes.func.isRequired,
    showNotification: PropTypes.func.isRequired,
    isSelected: PropTypes.func.isRequired,
    isAllChecked: PropTypes.func.isRequired,
    isIndeterminate: PropTypes.func.isRequired,
    handleSelectAll: PropTypes.func.isRequired,
    onSelectedAllContacts: PropTypes.func.isRequired,
    removeSelection: PropTypes.func.isRequired,
  };

  static defaultProps = {
    showContactReplyForCurrentSequence: false,
  };

  constructor(props) {
    super(props);
    const showFields = JSON.parse(Cookies.get('accountContactsShowFields') || '{}');

    this.state = {
      filterParams: {
        search: '',
        statuses: null,
        sequenceIds: null,
        campaignIds: null,
        deliveryStatuses: null,
        deliveryStatusCriteria: DEFAULT_DELIVERY_STATUS_CRITERIA,
        inboxCategories: null,
        validationStatuses: null,
        ordering: null,
      },
      deleteBulkModalOpen: false,
      blacklistBulkModalOpen: false,
      isContactEditModalOpen: false,
      contactUpdating: null,
      orderState: 'asc',
      fieldsToShow: {
        email: {
          show: isEmpty(showFields) || showFields.email,
          label: 'Email',
          orderingField: 'email',
        },
        outlet: {
          show: isEmpty(showFields) || showFields.outlet,
          label: 'Outlet',
          orderingField: 'outlet.name',
        },
        recentSequence: {
          show: isEmpty(showFields) || showFields.recentSequence,
          label: 'Recent Sequence',
          orderingField: 'recent_sequence.name',
        },
        deliveryStatus: {
          show: isEmpty(showFields) || showFields.deliveryStatus,
          label: 'Delivery Status',
          orderingField: 'delivery_status',
          headerContent: (
            <div className="flex items-center">
              Delivery Status
              <a
                // eslint-disable-next-line max-len
                href="https://help.prvolt.com/en/articles/4618926-analytics-definitions"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1"
              >
                <HelpOutlineRoundedIcon className="w-4 text-gray-400 hover:text-blue-900" />
              </a>
            </div>
          ),
        },
        inboxCategory: {
          show: isEmpty(showFields) || showFields.inboxCategory,
          label: 'Inbox Category',
          orderingField: 'inbox_category',
          headerContent: (
            <div className="flex items-center">
              Inbox Category
              <a
                // eslint-disable-next-line max-len
                href="https://help.prvolt.com/en/articles/4618926-analytics-definitions"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1"
              >
                <HelpOutlineRoundedIcon className="w-4 text-gray-400 hover:text-blue-900" />
              </a>
            </div>
          ),
        },
        errors: {
          show: isEmpty(showFields) || showFields.errors,
          label: 'Alerts',
        },
        contactSubject: {
          show: showFields.contactSubject,
          label: 'Contact Subject',
          orderingField: 'contact_subject',
        },
        outletType: {
          show: showFields.outletType,
          label: 'Outlet Type',
        },
        uvpm: {
          show: showFields.uvpm,
          label: 'UVPM',
          orderingField: 'outlet.unique_monthly_visitors',
        },
        audience: {
          show: showFields.audience,
          label: 'Audience',
          orderingField: 'outlet.audience',
        },
        da: {
          show: showFields.da,
          label: 'DA',
          orderingField: 'outlet.domain_authority',
        },
        country: {
          show: showFields.country,
          label: 'Country',
          orderingField: 'outlet.country',
        },
        dma: {
          show: showFields.dma,
          label: 'DMA',
          orderingField: 'outlet.dma',
        },
      },
      isExporting: false,
    };
    this.toggleContactEditModal = this.toggleContactEditModal.bind(this);
    this.parentComponentId = uuidv4(); // preventing duplicated popover IDs when reusing component multiple times
    this.fetchContacts = this.fetchContacts.bind(this);
  }

  componentDidUpdate() {
    if (this.props.isSelectedAllContacts && !this.props.isAllChecked()) {
      this.props.handleSelectAll();
    }
  }

  onResetSelectedFilter = (filter) => {
    const newParams = {};
    switch (filter) {
      case DYNAMIC_MENU_OPTIONS.campaign:
        newParams.campaignIds = null;
        break;
      case DYNAMIC_MENU_OPTIONS.sequence:
        newParams.sequenceIds = null;
        break;
      case DYNAMIC_MENU_OPTIONS.deliveryStatus:
        newParams.deliveryStatuses = null;
        newParams.deliveryStatusCriteria = DEFAULT_DELIVERY_STATUS_CRITERIA;
        break;
      case DYNAMIC_MENU_OPTIONS.category:
        newParams.inboxCategories = null;
        break;
      case DYNAMIC_MENU_OPTIONS.validationStatus:
        newParams.validationStatuses = null;
        break;
      case DYNAMIC_MENU_OPTIONS.search:
        newParams.search = '';
        break;
      default:
        break;
    }
    this.setState({ filterParams: { ...this.state.filterParams, ...newParams } });
    this.props.removeSelection();
  }

  toggleDeleteBulkModal = () => {
    const { deleteBulkModalOpen } = this.state;
    this.setState({
      deleteBulkModalOpen: !deleteBulkModalOpen,
    });
  }

  toggleBlacklistBulkModal = () => {
    const { blacklistBulkModalOpen } = this.state;
    this.setState({
      blacklistBulkModalOpen: !blacklistBulkModalOpen,
    });
  }

  handleOnResetFilters = () => {
    const { filterParams } = this.state;
    const newParams = filterParams;
    newParams.deliveryStatuses = null;
    newParams.deliveryStatusCriteria = DEFAULT_DELIVERY_STATUS_CRITERIA;
    newParams.inboxCategories = null;
    newParams.validationStatuses = null;
    newParams.campaignIds = null;
    newParams.sequenceIds = null;
    newParams.search = '';
    this.setState(
      { ...this.state, ...newParams },
      this.fetchContacts,
    );
    this.props.removeSelection();
  }

  handleChangeSearch = (search) => {
    const paramsForState = { ...this.state.filterParams, search };
    this.setState({ filterParams: paramsForState }, this.fetchContacts);
  }

  handleChangeDeliveryStatuses = (deliveryStatuses) => {
    const paramsForState = { ...this.state.filterParams, deliveryStatuses };
    this.setState({ filterParams: paramsForState }, this.fetchContacts);
  }

  handleChangeDeliveryStatusCriteria = (deliveryStatusCriteria) => {
    const paramsForState = { ...this.state.filterParams, deliveryStatusCriteria };
    if (!isEmpty(this.state.filterParams.deliveryStatuses)) {
      this.setState({ filterParams: paramsForState }, this.fetchContacts);
    } else {
      this.setState({ filterParams: paramsForState });
    }
  }

  handleOrdering = (ordering, e = null) => {
    // clicked on checkbox
    if (e && e.target.nodeName === 'INPUT') {
      return;
    }
    const orderState = this.state.orderState === 'asc' ? 'desc' : 'asc';
    this.setState(prevState => ({
      ...prevState,
      orderState,
      filterParams: {
        ...prevState.filterParams,
        ordering,
      },
    }), this.fetchContacts);
  };

  calculateOrdering = (property, sorting) => {
    if (sorting === 'desc' && property) {
      return `-${property}`;
    }
    return property;
  };

  handleChangeCategories = (inboxCategories) => {
    const paramsForState = { ...this.state.filterParams, inboxCategories };
    this.setState({ filterParams: paramsForState }, this.fetchContacts);
  }

  handleChangeValidationStatus = (validationStatuses) => {
    const paramsForState = { ...this.state.filterParams, validationStatuses };
    this.setState({ filterParams: paramsForState }, this.fetchContacts);
  }

  handleChangeSequences = (sequenceIds) => {
    const paramsForState = {
      ...this.state.filterParams,
      sequenceIds,
    };
    this.setState({ filterParams: paramsForState }, this.fetchContacts);
  }

  handleChangeCampaigns = (campaignIds) => {
    const paramsForState = {
      ...this.state.filterParams,
      campaignIds,
    };
    this.setState({ filterParams: paramsForState }, this.fetchContacts);
  }

  handleOnDeleteFilter = (filter) => {
    const { filterParams } = this.state;
    const newParams = filterParams;
    switch (filter) {
      case DYNAMIC_MENU_OPTIONS.campaign:
        newParams.campaignIds = null;
        break;
      case DYNAMIC_MENU_OPTIONS.sequence:
        newParams.sequenceIds = null;
        break;
      case DYNAMIC_MENU_OPTIONS.deliveryStatus:
        newParams.deliveryStatuses = null;
        newParams.deliveryStatusCriteria = DEFAULT_DELIVERY_STATUS_CRITERIA;
        break;
      case DYNAMIC_MENU_OPTIONS.category:
        newParams.inboxCategories = null;
        break;
      case DYNAMIC_MENU_OPTIONS.validationStatus:
        newParams.validationStatuses = null;
        break;
      case DYNAMIC_MENU_OPTIONS.search:
        newParams.search = '';
        break;
      default:
        break;
    }
    this.setState(
      { ...this.state, ...newParams },
      this.fetchContacts,
    );
    this.props.removeSelection();
  }

  fetchContacts = () => {
    const preparedParams = this.prepareParams(this.state.filterParams);
    this.props.getContacts(preparedParams);
  }

  toggleContactEditModal(open, contact) {
    this.setState({
      isContactEditModalOpen: open,
      contactUpdating: contact,
    });
  }

  prepareParams = (params) => {
    const prepParams = { ...params };
    // in API we will consume accounts as string ( 'accountID1, accountID2' )
    if (prepParams.inboxCategories) {
      prepParams.statuses = flattenKeyPairArray(prepParams.inboxCategories, 'value');
      delete prepParams.inboxCategories;
    }
    if (prepParams.validationStatuses) {
      prepParams.validation_statuses = flattenKeyPairArray(prepParams.validationStatuses, 'value');
      delete prepParams.validationStatuses;
    }
    if (prepParams.deliveryStatuses) {
      prepParams.delivery_statuses = flattenKeyPairArray(prepParams.deliveryStatuses, 'value');
      delete prepParams.deliveryStatuses;
    }

    if (prepParams.sequenceIds) {
      prepParams.sequence_ids = flattenKeyPairArray(prepParams.sequenceIds, 'value');
      delete prepParams.sequenceIds;
    }
    if (prepParams.campaignIds) {
      prepParams.campaign_ids = flattenKeyPairArray(prepParams.campaignIds, 'value');
      delete prepParams.campaignIds;
    }
    Object.keys(prepParams).forEach(key => (isEmpty(prepParams[key])) && delete prepParams[key]);
    if (prepParams.deliveryStatusCriteria && !isEmpty(prepParams.delivery_statuses)) {
      prepParams.delivery_statuses_not = prepParams.deliveryStatusCriteria.value;
      delete prepParams.deliveryStatusCriteria;
    }
    if (prepParams.ordering) prepParams.ordering = this.calculateOrdering(prepParams.ordering, this.state.orderState);

    return prepParams;
  }

  renderContacts(contacts) {
    const { getContacts, showNotification } = this.props;
    return (
      contacts.results.map((contact) => {
        const isSelected = this.props.isSelected(contact.id);
        const { handleSelectClick, showContactReplyForCurrentSequence } = this.props;
        return (
          <ContactRow
            key={`contact-row-${contact.id}`}
            seqContact={contact}
            isSelected={isSelected}
            parentComponentId={this.parentComponentId}
            handleSelectClick={handleSelectClick}
            showContactReplyForCurrentSequence={showContactReplyForCurrentSequence}
            getContacts={getContacts}
            showNotification={showNotification}
            toggleContactEditModal={() => this.toggleContactEditModal(true, contact.contact_id)}
            fieldsToShow={this.state.fieldsToShow}
          />
        );
      })
    );
  }

  handleCSVExport = async () => {
    this.setState(prevState => ({ ...prevState, isExporting: true }));
    const data = {
      sequenceContactIds: this.props.getSelectedContactsOnPage().map(sq => sq.id),
    };
    const params = { all: this.props.isSelectedAllContacts };
    const response = await axios.post(
      apiExportAccountSequenceContacts(this.props.account.id),
      data,
      { withCredentials: true, params, responseType: 'blob' },
    );


    // check if response is csv
    if (response.headers['content-type'] !== 'text/csv') {
      this.setState(prevState => ({ ...prevState, isExporting: false }));
      this.props.showNotification(
        `Your report is being generated, you will receive an email to ${this.props.user.email} with your report in approximately 2 minutes..`,
        'success',
      );
      return;
    }

    // Create a blob from the response
    const blob = new Blob([response.data], { type: 'text/csv' });
    downloadBlob(blob, 'export.csv');
    this.setState(prevState => ({ ...prevState, isExporting: false }));
  }

  render() {
    const {
      contacts,
      account,
      getContacts,
      getSelectedContactsOnPage,
      showNotification,
    } = this.props;
    const {
      isExporting,
      filterParams,
      deleteBulkModalOpen,
      blacklistBulkModalOpen,
      isContactEditModalOpen,
      contactUpdating,
    } = this.state;
    const selectedContacts = getSelectedContactsOnPage();
    const isLoading = contacts.loading || account.loading;
    const isSelectedOnPage = selectedContacts.length > 0;
    const showFieldsCount = Object.values(this.state.fieldsToShow).filter(field => field.show === true).length;
    return (
      <React.Fragment>
        <ContactUpdateDrawer
          contactId={contactUpdating}
          isContactEditModalOpen={isContactEditModalOpen}
          toggleContactEditModal={() => this.toggleContactEditModal(false, contactUpdating)}
          onUpdate={this.fetchContacts}
        />
        <DeleteBulkModal
          isOpen={deleteBulkModalOpen}
          getContacts={getContacts}
          toggle={this.toggleDeleteBulkModal}
          selectedContacts={selectedContacts}
          showNotification={showNotification}
          preparedParams={this.prepareParams(filterParams)}
          isSelectedAllContacts={this.props.isSelectedAllContacts}
          countContacts={this.props.accountContacts.count}
          removeSelection={this.props.removeSelection}
        />
        <BlacklistBulkModal
          isOpen={blacklistBulkModalOpen}
          getContacts={getContacts}
          toggle={this.toggleBlacklistBulkModal}
          selectedContacts={selectedContacts}
          showNotification={showNotification}
          preparedParams={this.prepareParams(filterParams)}
          isSelectedAllContacts={this.props.isSelectedAllContacts}
          countContacts={this.props.accountContacts.count}
          removeSelection={this.props.removeSelection}
        />
        <div className="sequence-contact-list">
          <DynamicFilter
            onResetSelectedFilter={this.onResetSelectedFilter}
            menuDescription="Filter by"
            onResetFilters={this.handleOnResetFilters}
            options={[
              {
                key: DYNAMIC_MENU_OPTIONS.deliveryStatus,
                menuLabel: 'Delivery Statuses',
                onDeleteFilter: this.handleOnDeleteFilter,
                filterCriteria: <Select
                  placeholder="Criteria"
                  onChange={this.handleChangeDeliveryStatusCriteria}
                  options={DELIVERY_STATUS_CRITERIA_OPTIONS}
                  value={filterParams.deliveryStatusCriteria}
                />,
                component: <Select
                  isMulti
                  placeholder="Delivery Statuses"
                  onChange={this.handleChangeDeliveryStatuses}
                  options={DELIVERY_STATUS_OPTIONS}
                  value={filterParams.deliveryStatuses}
                />,
              },
              {
                key: DYNAMIC_MENU_OPTIONS.category,
                menuLabel: 'Inbox category',
                onDeleteFilter: this.handleOnDeleteFilter,
                component: <Select
                  isMulti
                  placeholder="Inbox Category"
                  onChange={this.handleChangeCategories}
                  options={REPLY_STATUS_OPTIONS}
                  value={filterParams.inboxCategories}
                  isCategorySelect
                />,
              },
              {
                key: DYNAMIC_MENU_OPTIONS.validationStatus,
                menuLabel: 'Email validation status',
                onDeleteFilter: this.handleOnDeleteFilter,
                component: <Select
                  isMulti
                  placeholder="Email validation status"
                  onChange={this.handleChangeValidationStatus}
                  options={EMAIL_VALIDATION_STATUS_OPTIONS}
                  value={filterParams.validationStatuses}
                />,
              },
              {
                key: DYNAMIC_MENU_OPTIONS.search,
                menuLabel: 'Text search',
                onDeleteFilter: this.handleOnDeleteFilter,
                component: <SearchInput
                  onChange={this.handleChangeSearch}
                  value={filterParams.search}
                />,
              },
              {
                key: DYNAMIC_MENU_OPTIONS.sequence,
                menuLabel: 'Sequences',
                onDeleteFilter: this.handleOnDeleteFilter,
                component: <SequencesFilter
                  value={filterParams.sequenceIds}
                  onChange={this.handleChangeSequences}
                  accountId={this.props.account.id}
                />,
              },
              {
                key: DYNAMIC_MENU_OPTIONS.campaign,
                menuLabel: 'Campaigns',
                onDeleteFilter: this.handleOnDeleteFilter,
                component: <CampaignsFilter
                  value={filterParams.campaignIds}
                  onChange={this.handleChangeCampaigns}
                  accountId={this.props.account.id}
                />,
              },
            ]}
          />
          <div className="flex pt-6 justify-between">
            <HideFields
              fieldsToShow={this.state.fieldsToShow}
              onFieldsToShowChange={newFieldsToShow =>
                this.setState(prevState => ({ ...prevState, fieldsToShow: newFieldsToShow }))}
              cookieKey="accountContactsShowFields"
              style={{ minWidth: 'max-content' }}
            />
            <ContactStatsSlim
              totalContacts={contacts.count}
              optouts={contacts.optouts}
              blacklisted={contacts.blacklisted}
              bounces={contacts.bounces}
              delivered={contacts.delivered}
              opened={contacts.opened}
              replies={contacts.replies}
            />
          </div>
          <div className="actions">
            <div className="actions__right mt-2">
              {/* bulk actions will work with selected contacts on the current page only */}
              {isSelectedOnPage &&
                <ButtonMenu btnText="Bulk actions">
                  <MenuItem onClick={this.toggleDeleteBulkModal}>Delete</MenuItem>
                  <MenuItem onClick={this.toggleBlacklistBulkModal}>Blacklist</MenuItem>
                  <MenuItem onClick={isExporting ? () => {} : this.handleCSVExport}>
                    {isExporting ?
                      <span className="loading-dots">Exporting</span> :
                      <React.Fragment>Export to CSV</React.Fragment>
                    }
                  </MenuItem>
                </ButtonMenu>
              }
            </div>
          </div>

          <div className="material-table__wrap mt-4">
            {isLoading ? (
              <div className="loader"><LoadingIcon /></div>
              ) : (
              <>
                <Table className="volt-table">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        key="Name"
                        onClick={e => this.handleOrdering('full_name', e)}
                        className="flex"
                      >
                        <Checkbox
                          indeterminate={this.props.isIndeterminate()}
                          checked={this.props.isAllChecked()}
                          onChange={this.props.handleSelectAll}
                        />
                        <TableSortLabel
                          order={this.state.orderState}
                          orderBy={filterParams.ordering}
                          property="full_name"
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      {Object.values(this.state.fieldsToShow).map(field => (
                        field.show && (
                          <TableCell
                            key={field.label}
                            alignRight={field.align === 'right'}
                            onClick={field.orderingField ? () => this.handleOrdering(field.orderingField) : () => { }}
                            className="sortable truncate-text--200 whitespace-nowrap"
                          >
                            {field.orderingField ?
                              <TableSortLabel
                                order={this.state.orderState}
                                orderBy={filterParams.ordering}
                                property={field.orderingField}
                              >
                                {field.headerContent || field.label}
                              </TableSortLabel> : field.headerContent || field.label}
                          </TableCell>
                        )
                      ))}
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  {this.props.isAllChecked() &&
                    this.props.getSelectedContactsOnPage().length < this.props.accountContacts.count &&
                    <tr className="material-table__row--contacts-info">
                      {!this.props.isSelectedAllContacts ?
                        <td colSpan={showFieldsCount + 2}>
                          {contacts.results.length} contacts are selected on this page.
                          <span onClick={() => this.props.onSelectedAllContacts(true)} aria-hidden="true">
                            Click to select all {this.props.accountContacts.count}
                          </span>
                        </td> :
                        <td colSpan={showFieldsCount + 2}>
                          Selected all {this.props.accountContacts.count} contacts
                        </td>
                      }
                    </tr>
                  }
                  <TableBody>
                    {(contacts.results.length > 0 && !isLoading) &&
                      this.renderContacts(contacts)
                    }
                  </TableBody>
                </Table>
                <NoResults loading={isLoading} results={contacts.results} />
              </>
            )}
          </div>
          <Pagination
            items={this.props.contacts}
            onChangePage={this.props.getContacts}
            params={this.prepareParams(filterParams)}
          />
        </div>
      </React.Fragment>
    );
  }
}


export default compose(connect(
  state => ({
    sequences: state.sequences,
    account: state.account,
    accountContacts: state.accountContacts,
    user: state.user,
  }),
  {
    getAccountSequences,
  },
))(withContactList(AccountContactList));
